/**
 * Quick search mock state: the workspace's search history, the jobs started
 * this session, and the posts sent to the dashboard.
 *
 * In the prototype all three were `QuickSearchView`'s own `useState`; here they
 * are the "server" side, so they survive navigating to `Details` and back (the
 * prototype's component did not remount there either) and reset on reload, as
 * the prototype's did.
 */
import type { QuickSearchHistoryEntry, QuickSearchRequest } from '@/data-sources/contract';

import {
  QUICK_SEARCH_PERIODS,
  SESSION_RANGE,
  seedHistory,
  type QuickSearchResult,
} from '../engine/quickSearch';
import { REFERENCE_DATE } from '../engine/reference';

const MINUTE_MS = 60_000;

export type MockJob = {
  jobId: string;
  request: QuickSearchRequest;
  status: 'running' | 'done';
  /** Engine hits, snapshotted when the job completed (statuses are read live). */
  hits: readonly QuickSearchResult[] | null;
  historyEntryId: string | null;
};

type QuickSearchDb = {
  history: QuickSearchHistoryEntry[];
  jobs: Map<string, MockJob>;
  /** Post ids sent to the dashboard — keyed by post, so the fact survives the result set it came from. */
  added: Set<string>;
  /** Monotonic run counter: job ids, history ids and the mock clock below. */
  sequence: number;
  /** Pending completion timers, cleared by `resetQuickSearchDb`. */
  timers: Set<ReturnType<typeof setTimeout>>;
};

/**
 * The seeded `Date range` strings back to the days they name. `All time` is
 * `SESSION_RANGE`, the one range with no window.
 */
function rangeDaysOf(range: string): number | null {
  if (range === SESSION_RANGE) return null;
  const period = QUICK_SEARCH_PERIODS.find((p) => p.label === range);
  if (!period) throw new Error(`quick-search seed: unknown range "${range}"`);
  return period.days;
}

function seeded(): QuickSearchHistoryEntry[] {
  return seedHistory.map((entry) => ({
    id: entry.id,
    terms: [...entry.terms],
    channel: entry.channel,
    rangeDays: rangeDaysOf(entry.range),
    ranAt: new Date(entry.ranAt).toISOString(),
  }));
}

export const quickSearchDb: QuickSearchDb = {
  history: seeded(),
  jobs: new Map(),
  added: new Set(),
  sequence: 0,
  timers: new Set(),
};

/**
 * When run `n` of this session ran, on the mock clock.
 *
 * The prototype stamped session runs with `Date.now()` — the real clock — so a
 * search run today sorted a week or a year away from the seeded rows, which
 * walk back a day at a time from `REFERENCE_DATE`. Here a run happens on the
 * reference day, one minute after the previous one: always newer than every
 * seed (the newest is `REFERENCE_DATE` itself), strictly ordered, and identical
 * on every reload.
 */
export function ranAtOf(sequence: number): string {
  return new Date(REFERENCE_DATE.getTime() + sequence * MINUTE_MS).toISOString();
}

/** Back to the seeded state. Tests only. */
export function resetQuickSearchDb(): void {
  for (const timer of quickSearchDb.timers) clearTimeout(timer);
  quickSearchDb.timers.clear();
  quickSearchDb.history = seeded();
  quickSearchDb.jobs.clear();
  quickSearchDb.added.clear();
  quickSearchDb.sequence = 0;
}
